import IframeResizer from 'iframe-resizer-react';
import { type FunctionComponent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getIsEuTenant } from '../../../../../configuration/login/loginSlice';
import { useAppDispatch, useAssetsThunkDispatch } from '../../../../../configuration/setup/hooks';
import { trace } from '../../../../../configuration/setup/trace';
import { fetchAssets, fetchDevices } from '../../../actions/assets/Assets.actions';
import {
    closeEasyOnboardingDialog,
    getEasyOnboardingSelectedAssetId,
    setIsAssetReloadImminent,
} from '../../../reducers/assets/assetsSlice';
import UnderConstruction from './UnderConstruction';
import { ASSET_TYPE_TILES } from './configuration/assetTypeTileConfig';
import { TELEMATICS_TILES } from './configuration/telematicsTileConfig';
import type { AssetTypeTile, BrandTile, TelematicsTile } from './configuration/types';
import {
    BrandSearchParamType,
    CloseIframeMessageType,
    DeviceTypeSearchParamType,
    ExistingAssetIdSearchParamType,
    SkipUpsellingSearchParamType,
    TestAppSearchParamType,
    VehicleTypeSearchParamType,
} from './types';

interface FormStepConnectorSelectionProps {
    selectedTelematic: TelematicsTile | undefined;
    selectedAssetType: AssetTypeTile | undefined;
    selectedBrand: BrandTile | undefined;
}

const IFrameWrapper = (props: { iframeUrl: string }) => (
    <IframeResizer
        id={'connector-iframe-wrapper'}
        title={'connector-iframe-wrapper'}
        src={props.iframeUrl}
        className={'modal-body bg-lightest padding-0 height-75vh'}
        heightCalculationMethod={'lowestElement'}
        style={{ width: '1px', minWidth: '100%', border: '0' }}
        data-cy={'connector-iframe-wrapper'}
    />
);

const FormStepNoTelematics = () => (
    <div className='padding-20'>
        <h1>No telematic selected</h1>
        <UnderConstruction />
    </div>
);
export const getIFrameUrl = (
    selectedTelematic: TelematicsTile,
    selectedBrand: BrandTile | undefined,
    selectedAssetType: AssetTypeTile | undefined,
    selectedAssetId: string | undefined,
    skipUpselling: boolean
) => {
    const url = new URL(selectedTelematic.connectorUri);

    const params = new URLSearchParams();
    params.append(DeviceTypeSearchParamType, selectedTelematic.deviceType);

    if (selectedBrand) {
        params.append(BrandSearchParamType, selectedBrand.name);
    }
    if (selectedAssetType) {
        params.append(VehicleTypeSearchParamType, selectedAssetType.assetType);
    }
    if (selectedAssetId) {
        params.append(ExistingAssetIdSearchParamType, selectedAssetId);
    }
    if (selectedTelematic === TELEMATICS_TILES.rioPocketDriverTest) {
        params.append(TestAppSearchParamType, 'true');
    }
    if (skipUpselling) {
        params.append(SkipUpsellingSearchParamType, 'true');
    }

    // Move search params to hash and return
    const searchString = params.toString();
    return url.href.replace(url.search, '') + (searchString ? `?${searchString}` : '');
};
export const FormStepConnector: FunctionComponent<FormStepConnectorSelectionProps> = (
    props: FormStepConnectorSelectionProps
) => {
    const { selectedTelematic, selectedAssetType, selectedBrand } = props;
    const appDispatch = useAppDispatch();
    const assetsThunkDispatch = useAssetsThunkDispatch();
    const selectedAssetId = useSelector(getEasyOnboardingSelectedAssetId);

    const isEuTenant = useSelector(getIsEuTenant);
    let tenantAdjustedTelematic = selectedTelematic;
    if (tenantAdjustedTelematic === undefined && !isEuTenant) {
        tenantAdjustedTelematic =
            selectedAssetType === ASSET_TYPE_TILES.trailer ? TELEMATICS_TILES.noTelematics : TELEMATICS_TILES.rioBox;
    }

    useEffect(() => {
        const handleMessageFromIFrame = (event: MessageEvent<{ type: string }>) => {
            if (event.data.type === CloseIframeMessageType) {
                trace('received CLOSE_IFRAME message. Closing the iframe now.');
                appDispatch(closeEasyOnboardingDialog());
                appDispatch(setIsAssetReloadImminent(true)); // gets reset with fetchDevices()
                setTimeout(
                    () =>
                        assetsThunkDispatch(fetchAssets({ force: true })).then(() =>
                            assetsThunkDispatch(fetchDevices({ force: true }))
                        ),
                    2000
                );
            }
        };
        window.addEventListener('message', handleMessageFromIFrame);

        return () => window.removeEventListener('message', handleMessageFromIFrame);
    }, [appDispatch, assetsThunkDispatch]);

    if (tenantAdjustedTelematic !== undefined) {
        return (
            <IFrameWrapper
                iframeUrl={getIFrameUrl(
                    tenantAdjustedTelematic,
                    selectedBrand,
                    selectedAssetType,
                    selectedAssetId,
                    !isEuTenant
                )}
            />
        );
    } else {
        return <FormStepNoTelematics />;
    }
};
